import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  Divider,
} from "@mui/material";

import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import PairPicker from "./PairPicker";
import KLineChartBot from "./KLineChartBot";
import SignalTerminalReport from "./SignalTerminalReport";
import { fetchSelectedTokensWithCache } from "./utils";

import { useStreamSignal } from "./useStreamSignal";
// { start, reset, starting, status, eventId, eventDoc, messages, error }

const GLASS = {
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const TF_LIST = ["15m", "1h", "4h", "1d"];

export default function StreamForecastTerminal({
  streamItem,
  defaultSymbol = "BTC",
  defaultTf = "1h",
  analysisType = "token_signal",
  allowedTypes = ["token_signal", "market_forecast", "top_forecasts", "chart_levels"],
  category = null,
}) {
  const [open, setOpen] = useState(false);

  // step: "menu" -> "form"
  const [step, setStep] = useState("menu");
  const [type, setType] = useState(null);

  // pair picker
  const btnRef = useRef(null);
  const [pairOpen, setPairOpen] = useState(false);
  const [tokenList, setTokenList] = useState([]);
  const [symbol, setSymbol] = useState(String(defaultSymbol || "BTC").toUpperCase());
  const [tf, setTf] = useState(defaultTf);

  const [riskMode, setRiskMode] = useState("moderate"); // optional

  const { start, reset, starting, status, eventId, eventDoc, messages, error } = useStreamSignal();

  // IMPORTANT: type for "signal" button is profile-driven
  const signalType = analysisType || "token_signal";

  // prompts: computed INSIDE component (hooks allowed here)
  const QUICK_PROMPTS_ALL = useMemo(
    () => [
      { id: signalType, text: "Want a signal or token analysis?" },
      { id: "market_forecast", text: "Market forecast" },
      { id: "top_forecasts", text: "Top crypto forecasts & bets" },
      { id: "chart_levels", text: "Get a chart with key levels" },
    ],
    [signalType]
  );

  const QUICK_PROMPTS = useMemo(() => {
    const allow = new Set((allowedTypes || []).map((x) => String(x)));
    return QUICK_PROMPTS_ALL.filter((q) => allow.has(q.id));
  }, [QUICK_PROMPTS_ALL, allowedTypes]);

  // load tokens
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await fetchSelectedTokensWithCache();
        if (mounted) setTokenList(Array.isArray(list) ? list : []);
      } catch {
        if (mounted) setTokenList([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // update symbol from streamItem
  useEffect(() => {
    if (streamItem?.symbol || streamItem?.asset) {
      const s = String(streamItem.symbol || streamItem.asset).toUpperCase();
      if (s) setSymbol(s);
    }
  }, [streamItem?.symbol, streamItem?.asset]);

  const statusChip = useMemo(() => {
    const s = String(status || "idle");
    const map = {
      idle: { label: "IDLE", tone: "rgba(148,163,184,.14)", border: "rgba(148,163,184,.28)", color: "#cbd5e1" },
      queued: { label: "QUEUED", tone: "rgba(251,191,36,.12)", border: "rgba(251,191,36,.40)", color: "#fde68a" },
      running: { label: "RUNNING", tone: "rgba(59,130,246,.12)", border: "rgba(59,130,246,.35)", color: "#bfdbfe" },
      done: { label: "DONE", tone: "rgba(34,197,94,.12)", border: "rgba(34,197,94,.35)", color: "#bbf7d0" },
      error: { label: "ERROR", tone: "rgba(239,68,68,.14)", border: "rgba(239,68,68,.35)", color: "#fecaca" },
    };
    return map[s] || map.idle;
  }, [status]);

  const onOpen = () => {
    setOpen((v) => !v);
    if (!open) {
      setStep("menu");
      setType(null);
    }
  };

  const onPickPrompt = (t) => {
    setType(t);
    setStep("form");
  };

  const run = async () => {
    if (!type) return;

    // needs symbol depending on type (NOT category)
    const needsSymbol = type === signalType || type === "chart_levels";

    await start({
      type,
      symbol: needsSymbol ? symbol : null,
      tf,
      riskMode,
      context: {
        streamId: streamItem?.id || null,
        category: category || null,
      },
    });
  };

  const showPair = type === signalType || type === "chart_levels";
  const showChart = type === "chart_levels";
  const showRisk = type === signalType;

  const result = eventDoc?.result || null;

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,.10)",
        p: 2,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, rgba(16,185,129,.18) 0%, rgba(59,130,246,.12) 40%, rgba(2,6,23,.92) 100%)",
        boxShadow: "0 14px 46px rgba(2,8,23,.55)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: -2,
          background:
            "radial-gradient(600px 220px at 20% 0%, rgba(34,197,94,.22), transparent 60%), radial-gradient(520px 220px at 100% 40%, rgba(59,130,246,.18), transparent 55%)",
          pointerEvents: "none",
          filter: "blur(2px)",
        }}
      />

      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 1.2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              border: "1px solid rgba(255,255,255,.14)",
              bgcolor: "rgba(255,255,255,.06)",
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 18, color: "rgba(229,231,235,.95)" }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 950,
                fontSize: 14,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                lineHeight: 1.1,
              }}
            >
              AI Terminal
            </Typography>

            <Typography sx={{ color: alpha(GLASS.text, 0.78), fontSize: 12.5 }}>
              Get → choose prompt → run → readable result
            </Typography>
          </Box>

          <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              size="small"
              label={statusChip.label}
              sx={{
                height: 22,
                borderRadius: 1,
                bgcolor: statusChip.tone,
                border: `1px solid ${statusChip.border}`,
                color: statusChip.color,
                "& .MuiChip-label": { px: 0.8, fontWeight: 900, fontSize: 11, letterSpacing: ".06em" },
              }}
              variant="outlined"
            />

            <Button
              variant="contained"
              onClick={onOpen}
              sx={{
                fontWeight: 950,
                textTransform: "none",
                borderRadius: 2,
                px: 2.2,
                height: 40,
                background: "linear-gradient(135deg,#22c55e,#4ade80)",
                color: "#020617",
                boxShadow: "0 10px 28px rgba(34,197,94,.25)",
                "&:hover": { filter: "brightness(1.05)" },
              }}
            >
              {open ? "Close" : "Get"}
            </Button>
          </Box>
        </Box>

        <Collapse in={open} timeout={180} unmountOnExit>
          <Box
            sx={{
              mt: 1,
              borderRadius: 2.5,
              border: "1px solid rgba(255,255,255,.10)",
              bgcolor: "rgba(2,6,23,.50)",
              backdropFilter: "blur(8px)",
              overflow: "hidden",
            }}
          >
            {step === "menu" && (
              <Box sx={{ p: 1.2, display: "grid", gap: 1 }}>
                <Typography sx={{ fontWeight: 900, fontSize: 12.5, color: "#e5e7eb" }}>
                  Choose a request:
                </Typography>

                <Box sx={{ display: "grid", gap: 0.8 }}>
                  {QUICK_PROMPTS.map((q) => (
                    <Box key={q.id} sx={{ display: "flex", justifyContent: "flex-start" }}>
                      <Button
                        onClick={() => onPickPrompt(q.id)}
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          fontWeight: 900,
                          borderRadius: 2,
                          borderColor: "rgba(255,255,255,.14)",
                          color: "#e5e7eb",
                          bgcolor: "rgba(255,255,255,.03)",
                          "&:hover": { bgcolor: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.22)" },
                        }}
                      >
                        {q.text}
                      </Button>
                    </Box>
                  ))}
                </Box>

                <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>
                  After you pick one, you’ll get the right controls (pair / chart / forecast).
                </Typography>
              </Box>
            )}

            {step === "form" && (
              <Box sx={{ p: 1.2, display: "grid", gap: 1.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    onClick={() => {
                      setStep("menu");
                      setType(null);
                    }}
                    startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
                    variant="text"
                    sx={{ textTransform: "none", fontWeight: 900, color: "rgba(229,231,235,.9)" }}
                  >
                    Back
                  </Button>

                  <Chip
                    size="small"
                    label={String(type || "").toUpperCase()}
                    variant="outlined"
                    sx={{
                      height: 22,
                      borderRadius: 1,
                      borderColor: "rgba(255,255,255,.14)",
                      color: "#cbd5e1",
                      "& .MuiChip-label": { px: 0.7, fontWeight: 900, fontSize: 10.5, letterSpacing: ".05em" },
                    }}
                  />

                  <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      onClick={() => reset()}
                      disabled={starting}
                      startIcon={<RestartAltIcon />}
                      variant="text"
                      sx={{ textTransform: "none", fontWeight: 900, color: "rgba(229,231,235,.85)" }}
                    >
                      Reset
                    </Button>

                    <Button
                      onClick={run}
                      disabled={starting || (showPair && !symbol)}
                      startIcon={<PlayArrowIcon />}
                      variant="contained"
                      sx={{
                        textTransform: "none",
                        fontWeight: 950,
                        borderRadius: 2,
                        background: "linear-gradient(135deg,#22c55e,#4ade80)",
                        color: "#020617",
                        "&:hover": { filter: "brightness(1.05)" },
                      }}
                    >
                      Run
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,.08)" }} />

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {showPair && (
                    <>
                      <Button
                        ref={btnRef}
                        variant="outlined"
                        onClick={() => setPairOpen(true)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 900,
                          borderRadius: 2,
                          borderColor: "rgba(255,255,255,.14)",
                          color: "#e5e7eb",
                          bgcolor: "rgba(255,255,255,.03)",
                          "&:hover": { bgcolor: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.22)" },
                        }}
                      >
                        Pair: {symbol}/USDT
                      </Button>

                      <PairPicker
                        anchorEl={btnRef.current}
                        open={pairOpen}
                        onClose={() => setPairOpen(false)}
                        list={tokenList}
                        onSelect={(t) => {
                          const s = String(t?.symbol || "").toUpperCase();
                          if (s) setSymbol(s);
                        }}
                      />
                    </>
                  )}

                  <ToggleButtonGroup
                    value={tf}
                    exclusive
                    onChange={(_, v) => v && setTf(v)}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        height: 32,
                        borderRadius: "10px !important",
                        fontSize: 11.5,
                        fontWeight: 900,
                        color: "#e5e7eb",
                        borderColor: "rgba(255,255,255,.14)",
                        "&.Mui-selected": {
                          background: "rgba(147,197,253,.18)",
                          borderColor: "rgba(147,197,253,.45)",
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    {TF_LIST.map((x) => (
                      <ToggleButton key={x} value={x}>
                        {x}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>

                  {showRisk && (
                    <ToggleButtonGroup
                      value={riskMode}
                      exclusive
                      onChange={(_, v) => v && setRiskMode(v)}
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          height: 32,
                          borderRadius: "10px !important",
                          fontSize: 11.5,
                          fontWeight: 900,
                          color: "#e5e7eb",
                          borderColor: "rgba(255,255,255,.14)",
                          "&.Mui-selected": {
                            background: "rgba(255,255,255,.08)",
                            borderColor: "rgba(255,255,255,.22)",
                            color: "#fff",
                          },
                        },
                      }}
                    >
                      <ToggleButton value="conservative">Low</ToggleButton>
                      <ToggleButton value="moderate">Mid</ToggleButton>
                      <ToggleButton value="aggressive">High</ToggleButton>
                    </ToggleButtonGroup>
                  )}

                  {starting && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>running…</Typography>
                    </Box>
                  )}
                </Box>

                {showChart && (
                  <KLineChartBot symbol={symbol} interval={tf} height={420} theme="dark" locale="en-US" />
                )}

                <SignalTerminalReport
                  type={type}
                  symbol={symbol}
                  tf={tf}
                  eventId={eventId}
                  status={status}
                  error={error}
                  result={result}
                  messages={messages}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

